import React, { useState } from 'react';
import { GammaLensLogo } from './LandingPage';

const SECTIONS = [
  { id: 'disclaimer', label: 'Disclaimer' },
  { id: 'terms', label: 'Terms of Service' },
  { id: 'privacy', label: 'Privacy Policy' },
];

export default function LegalPage({ onBack }) {
  const [activeSection, setActiveSection] = useState('disclaimer');

  return (
    <div style={{
      minHeight: '100vh', background: '#030712', color: '#cbd5e1',
      fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
@media(max-width:768px){.legal-layout{flex-direction:column!important}.legal-sidebar{position:static!important;width:100%!important}}`}</style>

      {/* Header */}
      <header style={{
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        padding: '14px 28px',
        display: 'flex', alignItems: 'center', gap: '12px',
        position: 'sticky', top: 0, background: '#030712ee',
        backdropFilter: 'blur(12px)', zIndex: 100,
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', color: '#64748b',
          cursor: 'pointer', fontSize: '14px', padding: '4px 8px',
        }}>
          ← Home
        </button>
        <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.06)' }} />
        <GammaLensLogo size={24} />
        <span style={{ fontSize: '15px', fontWeight: '700', color: '#f1f5f9' }}>Legal</span>
      </header>

      <div className="legal-layout" style={{
        maxWidth: '960px', margin: '0 auto', padding: '32px 24px',
        display: 'flex', gap: '32px',
      }}>
        {/* Sidebar Nav */}
        <nav className="legal-sidebar" style={{
          position: 'sticky', top: '80px', alignSelf: 'flex-start',
          minWidth: '180px',
        }}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => {
                setActiveSection(s.id);
                document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 14px', marginBottom: '4px',
                background: activeSection === s.id ? 'rgba(37,99,235,0.08)' : 'transparent',
                border: 'none',
                borderLeft: activeSection === s.id ? '2px solid #3b82f6' : '2px solid transparent',
                borderRadius: '0 8px 8px 0',
                color: activeSection === s.id ? '#f1f5f9' : '#64748b',
                fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {s.label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '12px', color: '#475569', marginBottom: '32px' }}>
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          {/* ── DISCLAIMER ── */}
          <section id="disclaimer" style={{ marginBottom: '48px' }}>
            <h2 style={h2Style}>Disclaimer</h2>

            <div style={cardStyle}>
              <h3 style={h3Style}>Not Financial Advice</h3>
              <p style={pStyle}>
                GammaLens ("the Service") provides educational content and analytical tools related to options market 
                mechanics, including Gamma Exposure (GEX) levels, Delta Exposure (DEX) levels, and AI-generated 
                market interpretations. <strong style={{ color: '#f1f5f9' }}>None of the content provided by GammaLens 
                constitutes financial advice, investment advice, trading advice, or any other form of professional advice.</strong>
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>Educational Purpose Only</h3>
              <p style={pStyle}>
                All information presented on GammaLens — including but not limited to GEX levels, key levels 
                (Call Wall, Put Wall, Gamma Flip), trade setup cards, AI-generated market insights, and accuracy 
                tracking data — is provided strictly for <strong style={{ color: '#f1f5f9' }}>educational and informational purposes</strong>.
              </p>
              <p style={pStyle}>
                Setup cards displaying potential entries, targets, and stops are 
                <strong style={{ color: '#f1f5f9' }}> hypothetical educational scenarios</strong> derived from 
                mathematical analysis of public options data. They are not recommendations to buy, sell, or hold 
                any security. They do not account for your individual financial situation, risk tolerance, or 
                investment objectives. Users should treat setup cards as one input among many in their own research process.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>No Guarantee of Accuracy</h3>
              <p style={pStyle}>
                GEX and DEX levels are calculated from publicly available options market data. While we strive for 
                accuracy, we make no representations or warranties regarding the completeness, accuracy, reliability, 
                or timeliness of any information provided. Options data can be delayed, incomplete, or subject to 
                errors from upstream data providers.
              </p>
              <p style={pStyle}>
                Past performance of GEX levels, as shown in our accuracy tracker, does not guarantee future results. 
                Market conditions can change rapidly, and levels that held in the past may not hold in the future.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>AI-Generated Content</h3>
              <p style={pStyle}>
                Market interpretations, regime analysis, and setup card analysis are generated by artificial intelligence (AI) systems. 
                AI-generated content may contain errors, inaccuracies, or omissions. It should not be relied upon as the 
                sole basis for any trading or investment decision. Always verify AI-generated analysis with your own 
                research and judgment.
              </p>
              <p style={pStyle}>
                AI-generated content on GammaLens does not constitute a recommendation to buy, sell, or hold any security. 
                References to "bias," "direction," or "regime" describe mathematical relationships in options positioning data — 
                they are not predictions of future price movement.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>Risk of Loss</h3>
              <p style={pStyle}>
                Trading securities, including options and equities, involves substantial risk of loss and is not suitable 
                for every investor. You could lose some or all of your invested capital. <strong style={{ color: '#f1f5f9' }}>You 
                should only trade with money you can afford to lose.</strong>
              </p>
              <p style={pStyle}>
                Before making any investment decision, you should consult with a qualified financial advisor who 
                understands your personal financial situation.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>No Fiduciary Relationship</h3>
              <p style={pStyle}>
                Use of GammaLens does not create a fiduciary relationship, advisory relationship, or any other 
                professional relationship between you and GammaLens or its operators. We are not registered as 
                an investment advisor, broker-dealer, or financial planner with any regulatory authority.
              </p>
            </div>
          </section>

          {/* ── TERMS OF SERVICE ── */}
          <section id="terms" style={{ marginBottom: '48px' }}>
            <h2 style={h2Style}>Terms of Service</h2>

            <div style={cardStyle}>
              <h3 style={h3Style}>1. Acceptance of Terms</h3>
              <p style={pStyle}>
                By accessing or using GammaLens (accessible at gammalens.markets), you agree to be bound by these 
                Terms of Service. If you do not agree to these terms, do not use the Service.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>2. Description of Service</h3>
              <p style={pStyle}>
                GammaLens provides educational tools for analyzing options market data, including Gamma Exposure 
                calculations, key level identification, AI-powered market interpretation, accuracy tracking, and 
                educational content about options market mechanics. The Service is provided on an "as is" and 
                "as available" basis.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>3. User Accounts</h3>
              <p style={pStyle}>
                You may create a free account to access additional features. You are responsible for maintaining 
                the confidentiality of your account credentials and for all activities that occur under your account. 
                You must provide accurate information when creating your account and must be at least 18 years old.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>4. Acceptable Use</h3>
              <p style={pStyle}>You agree not to:</p>
              <ul style={ulStyle}>
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to scrape, reverse engineer, or extract data from the Service through automated means</li>
                <li>Redistribute, resell, or republish GammaLens data or analysis without permission</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or its infrastructure</li>
              </ul>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>5. Intellectual Property</h3>
              <p style={pStyle}>
                All content, analysis, algorithms, design, and branding on GammaLens are the intellectual property 
                of GammaLens and its operators. You may not copy, modify, distribute, or create derivative works 
                from our content without explicit written permission.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>6. Limitation of Liability</h3>
              <p style={pStyle}>
                To the fullest extent permitted by law, GammaLens and its operators shall not be liable for any 
                direct, indirect, incidental, special, consequential, or punitive damages arising from your use 
                of the Service, including but not limited to financial losses from trading decisions made using 
                information provided by the Service.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>7. Data Sources & Third Parties</h3>
              <p style={pStyle}>
                GammaLens relies on third-party data providers for options market data. We do not guarantee the 
                accuracy, completeness, or availability of third-party data. Service availability may be affected 
                by upstream data provider issues.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>8. Modifications</h3>
              <p style={pStyle}>
                We reserve the right to modify these Terms at any time. Continued use of the Service after 
                changes constitutes acceptance of the updated Terms. We will make reasonable efforts to notify 
                users of material changes.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>9. Termination</h3>
              <p style={pStyle}>
                We reserve the right to suspend or terminate your access to the Service at any time, with or 
                without cause, with or without notice.
              </p>
            </div>
          </section>

          {/* ── PRIVACY POLICY ── */}
          <section id="privacy" style={{ marginBottom: '48px' }}>
            <h2 style={h2Style}>Privacy Policy</h2>

            <div style={cardStyle}>
              <h3 style={h3Style}>1. Information We Collect</h3>
              <p style={pStyle}><strong style={{ color: '#f1f5f9' }}>Account Information:</strong> When you create an account, we collect your email address, display name (optional), and a hashed version of your password. We never store passwords in plain text.</p>
              <p style={pStyle}><strong style={{ color: '#f1f5f9' }}>Usage Data:</strong> We collect anonymous usage data through Google Analytics 4, including pages visited, features used, and general interaction patterns. This helps us improve the Service.</p>
              <p style={pStyle}><strong style={{ color: '#f1f5f9' }}>Waitlist Data:</strong> If you joined our waitlist, we collected your email address through Brevo (formerly Sendinblue).</p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>2. How We Use Your Information</h3>
              <ul style={ulStyle}>
                <li>To provide and maintain the Service</li>
                <li>To authenticate your account</li>
                <li>To send service-related communications (e.g., launch notifications)</li>
                <li>To improve the Service based on usage patterns</li>
                <li>To track accuracy data and display aggregated results</li>
              </ul>
              <p style={pStyle}>We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>3. Data Storage & Security</h3>
              <p style={pStyle}>
                Your data is stored on secure servers provided by Render (backend and database) and Vercel (frontend). 
                Passwords are hashed using industry-standard algorithms. We use JWT tokens for authentication, 
                transmitted over HTTPS.
              </p>
              <p style={pStyle}>
                While we implement reasonable security measures, no method of electronic storage or transmission 
                is 100% secure. We cannot guarantee absolute security of your data.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>4. Third-Party Services</h3>
              <p style={pStyle}>GammaLens uses the following third-party services that may process your data:</p>
              <ul style={ulStyle}>
                <li><strong style={{ color: '#f1f5f9' }}>Render</strong> — Backend hosting and PostgreSQL database</li>
                <li><strong style={{ color: '#f1f5f9' }}>Vercel</strong> — Frontend hosting</li>
                <li><strong style={{ color: '#f1f5f9' }}>Google Analytics 4</strong> — Anonymous usage analytics</li>
                <li><strong style={{ color: '#f1f5f9' }}>Brevo</strong> — Email communications</li>
                <li><strong style={{ color: '#f1f5f9' }}>Anthropic (Claude AI)</strong> — AI-generated market analysis (no personal data is sent to AI)</li>
                <li><strong style={{ color: '#f1f5f9' }}>MarketData.app</strong> — Options market data provider</li>
              </ul>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>5. Cookies & Tracking</h3>
              <p style={pStyle}>
                We use localStorage to store your authentication token and user preferences. Google Analytics 
                may use cookies for anonymous usage tracking. You can disable cookies in your browser settings, 
                though this may affect Service functionality.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>6. Data Retention</h3>
              <p style={pStyle}>
                We retain your account data for as long as your account is active. If you wish to delete your 
                account and associated data, contact us at the email below. Usage analytics data is retained 
                in aggregated, anonymized form.
              </p>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>7. Your Rights</h3>
              <p style={pStyle}>You have the right to:</p>
              <ul style={ulStyle}>
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and personal data</li>
                <li>Opt out of non-essential communications</li>
              </ul>
            </div>

            <div style={cardStyle}>
              <h3 style={h3Style}>8. Contact</h3>
              <p style={pStyle}>
                For questions about these policies, data requests, or any concerns, contact us at:{' '}
                <strong style={{ color: '#3b82f6' }}>support@gammalens.markets</strong>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/* ── STYLES ── */
const h2Style = {
  fontSize: '24px', fontWeight: '800', color: '#f1f5f9',
  marginBottom: '20px', letterSpacing: '-0.5px',
  paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const h3Style = {
  fontSize: '15px', fontWeight: '700', color: '#f1f5f9',
  marginBottom: '10px',
};

const pStyle = {
  fontSize: '14px', color: '#94a3b8', lineHeight: 1.8,
  margin: '0 0 12px',
};

const ulStyle = {
  fontSize: '14px', color: '#94a3b8', lineHeight: 1.8,
  margin: '0 0 12px', paddingLeft: '20px',
};

const cardStyle = {
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.04)',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '12px',
};
